import { useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { fetchBotCard, unlockBotCard, activateBotCard, reportBotCard, regenerateBotCardPhoto } from 'api/bot';
import { BotCard } from 'api/bot/types';
import { PageHolder } from 'components/skeletons/PageHolder';
import { Creator } from 'components/Creator';
import { useModalsStore } from 'store/modals';
import { useAuthStore } from 'store/auth';
import { reportBug } from 'utils';
import { ClipLoader } from 'react-spinners';
import { Copy } from 'components/icons/Copy';
import { showToast } from 'components/ui/CustomToast';
import { fetchBot } from 'api/bot';
import { Bot } from 'api/bot/types';
import { Flag } from 'components/icons/Flag';
import { Edit } from 'components/icons/Edit';
import { Regenerate } from 'components/icons/Regenerate';
import { ConfirmModal } from 'components/modals/ConfirmModal';
import { Tag } from 'components/ui/Tag';

type ReportReason =
  // Card level reasons
  | 'age_related'
  | 'illegal_content'
  | 'low_quality'
  // BotCard level reasons
  | 'photo_nsfw'
  | 'photo_deformed';

const ReportModal = ({
  isOpen,
  onClose,
  onSubmit
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: ReportReason) => void;
}) => {
  const reasons: {label: string, value: ReportReason}[] = [
    { label: 'Scenario is Age Inappropriate', value: 'age_related' },
    { label: 'Scenario describes Illegal Activity', value: 'illegal_content' },
    { label: 'Scenario is Spam or Low Quality', value: 'low_quality' },
    { label: 'Photo shows nudity', value: 'photo_nsfw' },
    { label: 'Photo is deformed', value: 'photo_deformed' },
  ];

  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isOpen ? '' : 'hidden'}`}>
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-stone-900 rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Report this Card</h3>
        <div className="space-y-2">
          {reasons.map(({label, value}) => (
            <button
              key={value}
              onClick={() => setSelectedReason(value)}
              className={`w-full text-left p-3 rounded box-border ${
                selectedReason === value
                  ? 'bg-brand/20 outline outline-1 outline-brand'
                  : 'bg-black/20 hover:bg-black/30'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => {
              if (selectedReason) {
                onSubmit(selectedReason);
                onClose();
              }
            }}
            disabled={!selectedReason}
            className="btn primary contained disabled:opacity-50"
          >
            Report
          </button>
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-stone-400 hover:text-white"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

const BotCardDetail = () => {
  const router = useRouter();
  const { id: botId, key, 'bot-name': botName } = router.query;
  const [card, setCard] = useState<BotCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [activating, setActivating] = useState(false);

  const { user, isLoading } = useAuthStore();
  const [botLoading, setBotLoading] = useState(true);
  const [ botData, setBotData ] = useState<Bot | null>(null);
  const { setUpsellModal, setCreditsUpsellModal } = useModalsStore.getState();
  const { setIsOpenAuthModal } = useModalsStore();

  const [showReportModal, setShowReportModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const [isDesktop, setIsDesktop] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 768);
    setIsExpanded(window.innerWidth >= 768);
  }, []);

  // Parse bot name from URL parameter
  const botFirstName = useMemo(() => {
    if (!botName) return '';
    const name = Array.isArray(botName) ? botName[0] : botName;
    const firstName = name.split('-')[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
  }, [botName]);


  useEffect(() => {
    const id = Array.isArray(botId) ? botId[0] : botId;
    if (id) {
      fetchBot(id)
        .then(data => setBotData(data))
        .catch(() => {})
        .finally(() => setBotLoading(false));
    }
  }, [botId]);

  useEffect(() => {
    const id = Array.isArray(botId) ? botId[0] : botId;
    const cardKey = Array.isArray(key) ? key[0] : key;

    const MAX_RETRIES = 10;
    let retryCount = 0;
    let pollInterval: NodeJS.Timeout;

    const fetchCard = async () => {
      try {
        if (!id || !cardKey) {
          throw new Error('Missing Card ID');
        }

        const data = await fetchBotCard(id, cardKey);
        setCard(data);
        setLoading(false);

        // If photo_url is null, continue polling
        // If photo_url exists, clear the interval
        if (data.photo_url) {
          clearInterval(pollInterval);
        } else if (retryCount >= MAX_RETRIES) {
          clearInterval(pollInterval);
          console.warn(`Stopped polling after ${MAX_RETRIES} attempts - photo still not ready`);
        }

        retryCount++;
      } catch (error) {
      }
    };

    if (id && cardKey) {
      // Initial fetch
      fetchCard();

      // Set up polling every 10 seconds if no photo_url
      pollInterval = setInterval(() => {
        if (!card?.photo_url) {
          fetchCard();
        } else {
          clearInterval(pollInterval);
        }
      }, 10000);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [botId, key, user]);

  const handleUnlock = async () => {
    if (!user) {
      setIsOpenAuthModal(true);
      return;
    }

    setUnlocking(true);
    try {
      const id = Array.isArray(botId) ? botId[0] : botId;
      const cardKey = Array.isArray(key) ? key[0] : key;

      if (!id || !cardKey) {
        throw new Error('Missing bot ID or card key');
      }

      await unlockBotCard(id, cardKey);
      showToast({ type: 'success', message: 'Card Unlocked!' });
    } catch (error: any) {
      if (error?.data?.error_code === 'PREMIUM_ONLY') {
        setUpsellModal({ image: (card?.photo_url || ''), type: 'premium_photo' });
      } else if (error?.data?.error_code === 'NOT_ENOUGH_BALANCE') {
        if (user?.is_premium) {
          setCreditsUpsellModal(card?.photo_url || '');
        } else {
          setUpsellModal({ image: (card?.photo_url || ''), type: 'out_of_credit', showRefreshCountdown: true });
        }
      } else {
        reportBug({ msg: error?.data?.message, error, prodToast: true });
      }
      return Promise.reject(error);
    } finally {
      setUnlocking(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      const id = Array.isArray(botId) ? botId[0] : botId;
      const cardKey = Array.isArray(key) ? key[0] : key;

      if (!id || !cardKey) {
        throw new Error('Missing bot ID or card key');
      }

      await activateBotCard(id, cardKey);
      router.push(`/chat/${id}/${botName}`);
    } catch (error: any) {
      reportBug({ msg: error?.data?.message, error, prodToast: true });
      setActivating(false);
    }
  };

  const handleReport = async (reason: ReportReason) => {
    try {
      const id = Array.isArray(botId) ? botId[0] : botId;
      const cardKey = Array.isArray(key) ? key[0] : key;

      if (!id || !cardKey) {
        throw new Error('Missing bot ID or card key');
      }

      await reportBotCard(id, cardKey, reason);
      showToast({ type: 'success', message: 'We will review this report and take action if necessary.' });
    } catch (error) {
      reportBug({ msg: 'Something went wrong.', error, prodToast: true });
    } finally {
      setShowReportModal(false);
    }
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const id = Array.isArray(botId) ? botId[0] : botId;
      const cardKey = Array.isArray(key) ? key[0] : key;
      await regenerateBotCardPhoto(id as string, cardKey as string);
      setCard(prev => prev ? { ...prev, photo_url: undefined } : null);
      showToast({ type: 'success', message: 'Generating a new photo...' });
    } catch (error) {
      reportBug({ msg: 'Something went wrong.', error, prodToast: true });
    } finally {
      setShowRegenerateModal(false);
      setIsRegenerating(false);
    }
  };

  if (loading || !card) {
    return <PageHolder />;
  }

  return (
    <div className="container my-4 md:my-8">
      <Link
        href={`/c/${botId}/${botName}`}
        className="text-stone-400 hover:text-brand mb-6 inline-block"
      >
        ← Profile
      </Link>

      <div className="grid md:grid-cols-6 gap-8">
        {/* Left Column - Image */}
        <div className="relative flex justify-center md:col-span-2">
          <div className="max-w-[500px] w-full">
            {card.photo_url ? (
              <div className="relative">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  disabled={isDesktop}
                  className="block w-full relative"
                >
                  <img
                    src={card.photo_url}
                    alt={card.custom_attributes.tagline}
                    className={`w-full rounded-xl object-cover object-top ${
                      isExpanded ? 'aspect-full' : 'aspect-square'
                    }`}
                  />
                  {!isExpanded && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/80 to-transparent rounded-b-xl" />
                  )}
                </button>
                {user?.username === card.creator?.username && (
                  <button
                    onClick={() => setShowRegenerateModal(true)}
                    className="absolute top-4 right-4 p-2 rounded-full border border-white/30 bg-black/50 hover:bg-black/70 text-gray-400 hover:text-white transition-colors"
                    title="Generate a new Photo"
                  >
                    <Regenerate className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="w-full rounded-xl bg-black/20 aspect-[3/4] flex flex-col items-center justify-center gap-4">
                <ClipLoader size={32} color="#fff"/>
              </div>
            )}

          {isLoading || botLoading ? (
              <button className="btn primary contained w-full !p-3 !rounded-full my-4" disabled>
                <ClipLoader size={20} color="#fff"/>
              </button>
            ) : card.is_unlocked ? (
              <button
                onClick={handleActivate}
                disabled={activating}
                className="btn primary contained w-full !p-3 !rounded-full my-4"
              >
                {activating ? 'Loading...' : (
                  <>
                    Chat Now
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleUnlock}
                disabled={unlocking}
                className="btn primary contained w-full !p-3 !rounded-full my-4"
              >
                {unlocking ? 'Unlocking...' : (
                  <>
                    Unlock for { botData?.custom_attributes.default_card_id == card.id ?
                      <>Free</> :
                      <>5 <img src="/images/goldCoin.png" alt="Coin" className="size-5" /></>
                    }
                  </>
                )}
              </button>
            )}

            <Link
              href={`/cards/new?key=${card.key}`}
              className="text-stone-500 hover:text-brand text-sm text-center block mt-2 hover:underline"
            >
              Clone this Card with another Krush
            </Link>

            <div className="flex items-center justify-center gap-2 mt-2 text-sm text-stone-500">
              <span>Import Card ID: {card.key}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(card.key)
                    .then(() => showToast({ type: 'success', message: 'Card ID copied.' }));
                }}
                className="text-sm text-brand-400 hover:text-white"
              >
                <Copy/>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="flex flex-col gap-4 md:col-span-4">
          <div className="space-y-4 bg-black/20 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <h1 className="text-lg md:text-2xl font-bold text-white mb-2">
                {card.custom_attributes.tagline || 'Untitled Card'}
              </h1>
              {user?.username === card.creator?.username ? (
                <Link
                  href={`/cards/edit?key=${card.key}`}
                  className="p-2 rounded-full bg-black/40 hover:bg-brand/60 text-stone-400 hover:text-white"
                  title="Edit this Card"
                >
                  <Edit className="w-4 h-4" />
                </Link>
              ) : (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="p-2 rounded-full bg-black/40 hover:bg-brand/60 text-stone-400 hover:text-white"
                  title="Report an issue with this Card"
                >
                  <Flag className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="mt-4">
              {card.creator && (
                <Creator
                  username={card.creator.username}
                  imageUrl={card.creator.picture}
                  createdAt={card.created_at}
                  />
              )}
            </div>
          </div>

          {card.custom_attributes.creator_notes && (
            <div className="bg-black/20 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-2">Creator Notes</h3>
              <p className="text-stone-300">{card.custom_attributes.creator_notes}</p>
            </div>
          )}

          <div className="space-y-4 bg-black/20 rounded-lg p-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Scenario</h3>
              <p className="text-stone-300 text-sm">
                {card.custom_attributes.scenario.replace(/{character}/g, botFirstName)}
              </p>

              {card.custom_attributes.scenario_secret && (
                <div className="mt-2">
                  <details className="group">
                    <summary className="cursor-pointer text-stone-500 hover:text-brand/80 text-sm font-medium">
                      Show Spoilers
                    </summary>
                    <p className="mt-2 text-stone-300 text-sm pl-4 border-l-2 border-brand/20">
                      {card.custom_attributes.scenario_secret.replace(/{character}/g, botFirstName)}
                    </p>
                  </details>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Character Role</h3>
              <p className="text-stone-300 text-sm">
                {card.custom_attributes.bot_role.replace(/{character}/g, botFirstName)}
              </p>
            </div>

            { card.custom_attributes.user_role && (
              <div>
                <h3 className="text-lg font-semibold mb-2">User Role</h3>
                <p className="text-stone-300 text-sm">
                  {card.custom_attributes.user_role?.replace(/{character}/g, botFirstName)}
                </p>
              </div>
            )}
          </div>

          {card.custom_attributes.tags.length > 0 && (
            <div className="space-y-4 bg-black/20 rounded-lg p-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {card.custom_attributes.tags.map((tag) => (
                    <Tag key={tag} tag={tag} isCard={true} />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="">
            {/* Additional Details Section */}
            <div className="grid grid-cols-2 gap-4">
              {card.custom_attributes.personality && (
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-stone-400">Personality</p>
                  <p className="text-sm text-stone-300">{card.custom_attributes.personality}</p>
                </div>
              )}
              {card.custom_attributes.behavior && (
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-stone-400">Behavior</p>
                  <p className="text-sm text-stone-300">{card.custom_attributes.behavior}</p>
                </div>
              )}
              {card.custom_attributes.outfit && (
                <div className="bg-black/20 rounded-lg p-4">
                  <p className="text-stone-400">Outfit</p>
                  <p className="text-sm text-stone-300">{card.custom_attributes.outfit}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Single Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleReport}
      />

      {/* Add the confirmation modal */}
      {showRegenerateModal && (
        <ConfirmModal
          title="Generate New Photo"
          message="Are you sure you want to generate a new photo for this Card? The current photo will be replaced."
          onClose={() => setShowRegenerateModal(false)}
          onConfirm={handleRegenerate}
          isLoading={isRegenerating}
          nonDismissable
        />
      )}
    </div>
  );
};

export default BotCardDetail;